import type { RatingRootCause } from '@zendesk/zqa-services/reviews'

export const hasSelectableRootCauseId = (id: unknown) => id !== undefined && id !== null && String(id).trim().length > 0

export const getRetainedSingleSelectRootCause = <T extends { id?: unknown; cause?: string }>(
  rootCauses: T[],
): T | undefined => rootCauses.find(({ id, cause }) => hasSelectableRootCauseId(id) && !!cause?.trim())

const normalizeRootCause = (cause: string) => cause.trim()

const toTrimmedRootCause = <T extends { id?: unknown; cause?: string }>({ id, cause }: T) => {
  const trimmedCause = cause?.trim()
  if (!trimmedCause) return undefined

  return hasSelectableRootCauseId(id) ? { id, cause: trimmedCause } : { cause: trimmedCause }
}

const isSameRootCause = (
  savedRootCause: { id?: unknown; cause?: string },
  retainedRootCause: { id?: unknown; cause?: string },
) => {
  if (hasSelectableRootCauseId(savedRootCause.id)) {
    return String(savedRootCause.id) === String(retainedRootCause.id)
  }

  return normalizeRootCause(savedRootCause.cause || '') === normalizeRootCause(retainedRootCause.cause || '')
}

export const getSingleSelectRootCauseSummary = ({
  rootCause,
  retainedRootCause,
  disallowedOtherRootCause = '',
}: {
  rootCause?: RatingRootCause
  retainedRootCause?: { id?: unknown; cause?: string }
  disallowedOtherRootCause?: string
}) => {
  const retainedCause = retainedRootCause?.cause?.trim()
  const disallowedOther = normalizeRootCause(disallowedOtherRootCause)
  const retained = retainedCause && retainedCause !== disallowedOther ? [retainedCause] : []
  const savedRootCauses = [
    ...(rootCause?.predefined || []).map(toTrimmedRootCause),
    toTrimmedRootCause({ cause: rootCause?.other }),
  ].filter((rootCause): rootCause is { id: unknown; cause: string } | { cause: string; id?: undefined } => !!rootCause)
  let skippedRetainedRootCause = false

  const removed = savedRootCauses
    .filter((savedRootCause) => {
      if (retainedRootCause && !skippedRetainedRootCause && isSameRootCause(savedRootCause, retainedRootCause)) {
        skippedRetainedRootCause = true
        return false
      }

      return normalizeRootCause(savedRootCause.cause) !== disallowedOther
    })
    .map(({ cause }) => cause)

  return { retained, removed }
}
